import React, { createContext, SetStateAction, useContext, useEffect, useState } from 'react';
import { ScheduledSendError, findErrorClassification, ErrorType } from '../errorHandler';

type ValueProps = {
  formButtonsDisabled?: boolean;
  error?: ScheduledSendError;
  errorMessage: ErrorType;
  setError: React.Dispatch<SetStateAction<ScheduledSendError | undefined>>;
  setFormButtonsDisabled: React.Dispatch<SetStateAction<boolean>>;
};

const defaultValues: ValueProps = {
  formButtonsDisabled: false,
  error: undefined,
  errorMessage: 'default',
  setError: () => {},
  setFormButtonsDisabled: () => {}
};

export const ErrorContext = createContext(defaultValues);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

/*
  React component for handling error states inside ScheduleSendReport
  If there would be additional effects and error handlings, please put the logic inside this component
*/
export const ErrorProvider = ({ children }: { children: React.ReactNode }) => {
  /* Error state that contains the information about the error */
  const [error, setError] = useState<ScheduledSendError>();

  /* Error state for disabling the form buttons on the footer portion of Schedule Delivery Modal*/
  const [formButtonsDisabled, setFormButtonsDisabled] = useState<boolean>(false);

  /* Error message to be displayed in ErrorInlineMessage component */
  const [errorMessage, setErrorMessage] = useState<ErrorType>('default');

  const setDefaultErrorState = () => {
    setFormButtonsDisabled(false);
    setErrorMessage('default');
  };

  useEffect(() => {
    if (!error) {
      return;
    }

    const errorHasDetails = Boolean(error?.errorKey && error?.statusCode);

    // For cases where there is no details returned by backend (ex: network error, syntax error, other unhandled errors)
    if (!errorHasDetails) {
      setDefaultErrorState();
      return;
    }

    const errorClassification = findErrorClassification(error);

    // For cases where there are details returned by backend but we haven't classified it yet
    if (!errorClassification) {
      setFormButtonsDisabled(true);
      setErrorMessage('default');
      return;
    }

    setErrorMessage(errorClassification.type);
    setFormButtonsDisabled(errorClassification.disableFooterButtons);
  }, [error]);

  return (
    <ErrorContext.Provider
      value={{ error, setError, formButtonsDisabled, setFormButtonsDisabled, errorMessage }}
    >
      {children}
    </ErrorContext.Provider>
  );
};
